/****************************************************
 * Module Name:  watchlistModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg'
import Constants from "../../utility/constants";
import { Box, Button, styled } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import StoneDetailModal from "../common/stoneDetailModal";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },

}));

const WatchlistModal = ({ stoneData, handleSave, watchlistLoader, handleClose }) => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [stoneId, setStoneId] = useState();
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [watchlistData, setWatchlistData] = useState(stoneData);
    const [columnNames, setColumnNames] = useState([]);

    useEffect(() => {
        prepareColumns();
    }, []);

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            width: 80,
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80,
        },
        "8a492e00-6167-45de-b078-e572e47c8a39": {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 155,
            renderCell: renderCellExpand,
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            type: "number",
            align: "right",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            renderCell: renderCellExpand,
        },
        "d4992de9-92ab-4f9e-ba12-6c746e935611": {
            field: "colordescriptions",
            headerName: "COLOR DESCRIPTIONS",
            headerAlign: "center",
            align: "center",
            width: 160,
            renderCell: renderCellExpand,
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "right",
            type: "number",
            width: 50,
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            type: "number",
            width: 50,
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.row.rapnetprice
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            type: "number",
            width: 70,
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            type: "number",
            width: 70,
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        "ab3e506e-bddb-456d-b967-922dcee74d56": {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "left",
            width: 130,
            renderCell: renderCellExpand,
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            type: "number",
            width: 100,
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            type: "number",
            width: 75,
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            width: 75,
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        }
    };

    const prepareColumns = () => {
        let columnNames = [];
        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                columnNames.push(columnData);
            }
        });
        columnNames.push({
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 280,
            editable: true,
            renderCell: (params) => (
                <Box style={{ display: 'flex', alignItems: 'center', width: "17rem" }}>
                    <span style={{ width: "90%", textAlign: "center" }}>{params.value}</span>
                    {<EditIcon style={{ marginLeft: 10, fontSize: '1rem' }} />}
                </Box>
            ),
            cellClassName: (params) => 'lavenderCell'
        })
        setColumnNames(columnNames);
    }

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const processRowUpdate = (newRow, oldRow) => {
        const changedFeilds = Utility.getChangedFields(newRow, oldRow);
        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];

            let updatedRow;
            const updateWatchlistData = watchlistData.map((iStone) => {
                if (iStone.stoneid === id && value) {
                    if (field === "remark") {
                        updatedRow = {
                            ...iStone,
                            [field]: value,
                        };
                    }
                    return updatedRow;
                }
                return iStone;
            })
            setWatchlistData(updateWatchlistData);
            return updatedRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newRow };
        }
    };

    const addToWatchlist = () => {
        let data = watchlistData.map(iData => ({
            userid: Constants.loggedInUser.id,
            stoneid: iData.stoneid,
            remark: iData.remark
        }));

        if (handleSave) {
            handleSave(data);
        }
    }

    return (
        <Modal
            show={true}
            onHide={handleClose}
            backdrop="static"
            keyboard={true}
            enforceFocus={false}
            dialogClassName="responsive-modal" 
>

            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={() => setShowStoneDetailModal(false)}
                />
            ) : null}

            <Modal.Header closeButton>
                <Modal.Title>
                    <img
                        src={logo}
                        height="40pt"
                        width="40pt"
                        alt={Constants.COMPANY_SITE}
                    />
                    &nbsp;&nbsp;Add Stones to Watchlist
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        height: "400px"
                    }}
                >
                    <StyledDataGrid
                        disableRowSelectionOnClick={true}
                        density="compact"
                        slots={{ toolbar: GridToolbar }}
                        rows={watchlistData}
                        columns={columnNames}
                        getRowId={(row) => row.stoneid}
                        initialState={{
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    status: false,
                                    kapanid: false,
                                    packetid: false,
                                    location: false,
                                    depthpercent: false,
                                    tablepercent: false
                                }
                            }
                        }}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(error) => {
                            // console.error(error);
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        disableDensitySelector
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },                           
                            },
                        }}
                        apiRef={apiRef}
                        onCellClick={handleCellClick}
                        editMode="cell"
                    />
                </Box>
            </Modal.Body>
            <Modal.Footer>
                <Box
                    sx={{
                        "& .Mui-disabled": {
                            backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                            color: "rgba(0, 0, 0, 0.26) !important"
                        }
                    }}>
                    <LoadingButton
                        loading={watchlistLoader}
                        loadingPosition="start"
                        variant="contained"
                        onClick={() => addToWatchlist()}
                        type="Submit"
                        sx={{ mr: 1 }}
                        startIcon={<SaveIcon />}
                    >
                        Save
                    </LoadingButton>
                </Box>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleClose}
                    sx={{ mr: 2 }}
                    disabled={watchlistLoader}
                >
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default WatchlistModal;